/**
 * AgentStatsSection Component
 * Platform Insights-style dashboard: 4 KPI cards + Usage by Agent table
 */

import { useMemo } from 'react';
import { Card, Row, Col, Table, Empty, Typography, Tooltip, theme } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table/interface';
import { useLanguage } from '@/hooks/useLanguage';
import type { AgentStat, TimeseriesPoint } from '../types/insights';

const { Text } = Typography;

interface AgentStatsSectionProps {
  agentStats: AgentStat[];
  agentTimeseries: TimeseriesPoint[];
  loading: boolean;
  timeseriesLoading: boolean;
  error: string | null;
  /** Count from tenant-rankings (conversation-based, correct for shared bots) */
  activeTenantCount?: number;
  period?: number;
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString('en-US');
}

/** Compute period-over-period trend by splitting timeseries into first/second half. */
function computeKpiTrends(timeseries: TimeseriesPoint[]): {
  conversations: number | null;
  tokens: number | null;
  users: number | null;
} {
  const sorted = [...timeseries].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  if (sorted.length < 4) return { conversations: null, tokens: null, users: null };

  const mid = Math.floor(sorted.length / 2);
  const first = sorted.slice(0, mid);
  const second = sorted.slice(mid);

  const sumField = (pts: TimeseriesPoint[], field: keyof TimeseriesPoint) =>
    pts.reduce((s, p) => s + ((p[field] as number) ?? 0), 0);

  const pct = (a: number, b: number): number | null =>
    a === 0 ? null : ((b - a) / a) * 100;

  return {
    conversations: pct(sumField(first, 'conversations'), sumField(second, 'conversations')),
    tokens: pct(
      sumField(first, 'input_tokens') + sumField(first, 'output_tokens'),
      sumField(second, 'input_tokens') + sumField(second, 'output_tokens'),
    ),
    users: pct(sumField(first, 'users'), sumField(second, 'users')),
  };
}

/** Compute trend for a chatbot from timeseries: compare first half vs second half. */
function computeTrend(timeseries: TimeseriesPoint[], chatbotId: string): number | null {
  const pts = timeseries
    .filter(p => p.chatbot_id === chatbotId)
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  if (pts.length < 4) return null;
  const mid = Math.floor(pts.length / 2);
  const firstHalf = pts.slice(0, mid).reduce((s, p) => s + p.conversations, 0);
  const secondHalf = pts.slice(mid).reduce((s, p) => s + p.conversations, 0);
  if (firstHalf === 0) return null;
  return ((secondHalf - firstHalf) / firstHalf) * 100;
}

function TrendCell({ pct }: { pct: number | null }) {
  if (pct === null) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  const up = pct >= 0;
  return (
    <span style={{ color: up ? '#16a34a' : '#dc2626', fontWeight: 500, fontSize: 12 }}>
      {up ? <ArrowUpOutlined style={{ fontSize: 10 }} /> : <ArrowDownOutlined style={{ fontSize: 10 }} />}
      {' '}{Math.abs(pct).toFixed(0)}%
    </span>
  );
}

/** Small % badge shown inside KPI card */
function KpiBadge({ pct, tooltipTitle }: { pct: number | null; tooltipTitle: string }) {
  if (pct === null) return null;
  const up = pct >= 0;
  return (
    <Tooltip title={tooltipTitle}>
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        marginTop: 4,
        fontSize: 11,
        fontWeight: 600,
        color: up ? '#16a34a' : '#dc2626',
        background: up ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${up ? '#bbf7d0' : '#fecaca'}`,
        borderRadius: 20,
        padding: '1px 7px',
        cursor: 'default',
      }}>
        {up ? <ArrowUpOutlined style={{ fontSize: 9 }} /> : <ArrowDownOutlined style={{ fontSize: 9 }} />}
        {Math.abs(pct).toFixed(0)}%
        <InfoCircleOutlined style={{ fontSize: 9, opacity: 0.5, marginInlineStart: 1 }} />
      </span>
    </Tooltip>
  );
}

export function AgentStatsSection({
  agentStats,
  agentTimeseries,
  loading,
  error,
  activeTenantCount,
  period = 30,
}: AgentStatsSectionProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const totals = useMemo(() => agentStats.reduce(
    (acc, a) => ({
      conversations: acc.conversations + a.total_conversations,
      activeUsers: acc.activeUsers + a.active_users,
      inputTokens: acc.inputTokens + a.total_input_tokens,
      outputTokens: acc.outputTokens + a.total_output_tokens,
      cost: acc.cost + a.total_cost_usd,
    }),
    { conversations: 0, activeUsers: 0, inputTokens: 0, outputTokens: 0, cost: 0 }
  ), [agentStats]);

  const kpiTrends = useMemo(() => computeKpiTrends(agentTimeseries), [agentTimeseries]);

  // Use conversation-based tenant count (from tenant rankings) when available —
  // agentStats groups by chatbot.tenant_id which misattributes shared-bot usage.
  const tenantCount = activeTenantCount ?? 0;

  const halfPeriod = Math.round(period / 2);
  const trendTooltip = (metric: string) =>
    t('insights.agentStats.trendTooltip', { metric, half: halfPeriod, period });

  const kpiCards = [
    {
      label: t('insights.agentStats.conversations'),
      value: fmt(totals.conversations),
      trend: kpiTrends.conversations,
      trendTooltip: trendTooltip(t('insights.agentStats.conversations')),
    },
    {
      label: t('insights.agentStats.activeTenants'),
      value: fmt(tenantCount),
      trend: null,
      trendTooltip: '',
    },
    {
      label: t('insights.agentStats.activeUsers'),
      value: fmt(totals.activeUsers),
      trend: kpiTrends.users,
      trendTooltip: trendTooltip(t('insights.agentStats.activeUsers')),
    },
    {
      label: t('insights.agentStats.tokensUsed'),
      value: fmt(totals.inputTokens + totals.outputTokens),
      trend: kpiTrends.tokens,
      trendTooltip: trendTooltip(t('insights.agentStats.tokenUsage')),
    },
  ];

  const tableData = useMemo(() =>
    agentStats.map(a => ({
      ...a,
      trend: computeTrend(agentTimeseries, a.chatbot_id),
    })),
    [agentStats, agentTimeseries]
  );

  const columns: ColumnsType<typeof tableData[0]> = [
    {
      title: t('insights.agentStats.agent'),
      dataIndex: 'chatbot_name',
      key: 'chatbot_name',
      render: (name: string, row) => (
        <div>
          <Text style={{ fontWeight: 500, fontSize: 12 }}>{name}</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: 11 }} dir="auto">{row.tenant_name}</Text>
        </div>
      ),
    },
    {
      title: t('insights.agentStats.conversations'),
      dataIndex: 'total_conversations',
      key: 'total_conversations',
      sorter: (a, b) => a.total_conversations - b.total_conversations,
      defaultSortOrder: 'descend',
      render: (v: number) => <span style={{ fontSize: 12 }}>{v.toLocaleString('en-US')}</span>,
    },
    {
      title: t('insights.agentStats.tokens'),
      key: 'tokens',
      sorter: (a, b) => (a.total_input_tokens + a.total_output_tokens) - (b.total_input_tokens + b.total_output_tokens),
      render: (_: any, row) => <span style={{ fontSize: 12 }}>{fmt(row.total_input_tokens + row.total_output_tokens)}</span>,
    },
    {
      title: (
        <Tooltip title={`Trend — compares first vs second half of the ${period}-day window`}>
          <span>{t('insights.agentStats.trend')} <InfoCircleOutlined style={{ fontSize: 10, opacity: 0.5 }} /></span>
        </Tooltip>
      ),
      key: 'trend',
      width: 90,
      render: (_: any, row) => <TrendCell pct={row.trend} />,
    },
  ];

  const cardStyle = {
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgContainer,
  };

  const headerStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: token.colorTextTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '8px 14px',
    background: token.colorFillAlter,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
  };

  if (error) return (
    <Card bordered={false} style={{ ...cardStyle, marginBottom: 20 }}>
      <Empty description={error} />
    </Card>
  );

  return (
    <div style={{ marginBottom: 16 }}>
      {/* KPI cards */}
      <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
        {kpiCards.map(card => (
          <Col key={card.label} xs={24} sm={12} lg={6}>
            <Card
              loading={loading}
              bordered={false}
              style={cardStyle}
              bodyStyle={{ padding: '10px 14px' }}
            >
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', lineHeight: 1.2, marginBottom: 1 }} dir="auto">
                {card.value}
              </div>
              <div style={{ fontSize: 11, color: token.colorTextSecondary, marginBottom: card.trend !== null ? 4 : 0 }} dir="auto">
                {card.label}
              </div>
              <KpiBadge pct={card.trend} tooltipTitle={card.trendTooltip} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Usage by Agent table */}
      {!loading && agentStats.length > 0 && (
        <Card bordered={false} style={{ ...cardStyle, marginBottom: 0 }} bodyStyle={{ padding: 0 }}>
          <div style={headerStyle}>{t('insights.agentStats.usageByAgent')}</div>
          <Table
            columns={columns}
            dataSource={tableData}
            rowKey="chatbot_id"
            loading={loading}
            pagination={tableData.length > 20 ? { pageSize: 20, size: 'small' } : false}
            size="small"
            style={{ borderRadius: 0 }}
            showHeader
          />
        </Card>
      )}
    </div>
  );
}
